/**
 * The form inside the "ask our logistics team" dialog, plus its field rules.
 *
 * Split out of BookShipmentDedicatedLogisticsOffer, which was carrying both the
 * offer and the whole dialog. The rules live here rather than in Validators
 * because they are only ever these four fields: equipment type and
 * shared/dedicated are always required, while tarp and linear feet are required
 * only once the equipment type implies them.
 */

import { Button } from "@freightsimple/ui-core/Controls/Button";
import { ControlPresentationProvider } from "@freightsimple/ui-core/Controls/ControlPresentationProvider";
import { DimensionInput } from "@freightsimple/ui-core/Controls/DimensionInput";
import { TextArea } from "@freightsimple/ui-core/Controls/TextArea";
import { T } from "@freightsimple/ui-core/Typography/T";
import type { ErrorMessageType } from "@freightsimple/ui-core/FormLayout/FormItem";
import { Dimension, Units } from "@freightsimple/ui-core/Data/Units";
import { SharedDedicated } from "@freightsimple/generated-dashboard-openapi-client/models/SharedDedicated";
import { EquipmentType } from "@freightsimple/generated-dashboard-openapi-client/models/EquipmentType";
import type { ChangeEvent } from "react";
import { useState } from "react";
import { BookShipmentEquipmentTypeDropdown } from "../../Dropdowns/BookShipmentEquipmentTypeDropdown";
import { BookShipmentSharedDedicatedDropdown } from "../../Dropdowns/BookShipmentSharedDedicatedDropdown";
import { BookShipmentTarpingRequiredDropdown } from "../../Dropdowns/BookShipmentTarpingRequiredDropdown";

export interface ConfirmManualQuoteRequestDialogCallbackData {
  notes: string;
  equipmentType: EquipmentType;
  sharedDedicated: SharedDedicated;
  tarpRequired: boolean | undefined;
  linearFeet: number | undefined;
}

export interface ConfirmManualQuoteRequestDialogBodyProps {
  onFinished: (
    params: ConfirmManualQuoteRequestDialogCallbackData,
  ) => Promise<void>;
  onCancel: () => void;
  linearFeet: number | undefined;
}

function errorMessageForRequired<T>(value: T | undefined): ErrorMessageType {
  if (value === undefined) {
    return "Required";
  }

  return undefined;
}

function errorMessageForTarpRequired(
  equipmentType: EquipmentType | undefined,
  tarpRequired: undefined | boolean,
) {
  if (equipmentType !== EquipmentType.FlatBed) {
    return undefined;
  }

  return errorMessageForRequired(tarpRequired);
}

function errorMessageForLinearFeet(
  sharedDedicated: SharedDedicated | undefined,
  linearFeet: undefined | number,
): ErrorMessageType {
  if (sharedDedicated === SharedDedicated.Dedicated) {
    return undefined;
  }

  if (linearFeet === undefined) {
    return "Required";
  }

  if (linearFeet < 2) {
    return "Too Small";
  }

  if (linearFeet > 53) {
    return "Too Large";
  }
  return undefined;
}

export function ConfirmManualQuoteRequestDialogBody_(
  props: ConfirmManualQuoteRequestDialogBodyProps,
) {
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [equipmentType, setEquipmentType] = useState<
    EquipmentType | undefined
  >();
  const [sharedDedicated, setSharedDedicated] = useState<
    SharedDedicated | undefined
  >();
  const [tarpRequired, setTarpRequired] = useState<boolean | undefined>();
  const [linearFeet, setLinearFeet] = useState<number | undefined>(
    props.linearFeet,
  );
  const [forceValidation, setForceValidation] = useState(false);

  const equipmentTypeErrorMessage = errorMessageForRequired(equipmentType);
  const sharedDedicatedErrorMessage = errorMessageForRequired(sharedDedicated);
  const tarpRequiredErrorMessage = errorMessageForTarpRequired(
    equipmentType,
    tarpRequired,
  );

  const linearFeetErrorMessage = errorMessageForLinearFeet(
    sharedDedicated,
    linearFeet,
  );

  function isValid(): boolean {
    if (equipmentTypeErrorMessage !== undefined) {
      return false;
    }

    if (sharedDedicatedErrorMessage !== undefined) {
      return false;
    }

    if (tarpRequiredErrorMessage !== undefined) {
      return false;
    }

    if (linearFeetErrorMessage !== undefined) {
      return false;
    }

    return true;
  }

  async function onFinished() {
    setForceValidation(true);

    if (!isValid()) {
      return;
    }

    if (equipmentType === undefined || sharedDedicated === undefined) {
      // Shouldn't be here
      return;
    }

    setLoading(true);
    await props.onFinished({
      equipmentType,
      sharedDedicated,
      tarpRequired,
      linearFeet,
      notes,
    });
    setLoading(false);
  }
  return (
    // Customer-facing quote-flow form: pin the spacious underlined control
    // look (the quotes screen itself mounts no presentation provider, and the
    // ui-core default would render these dropdowns boxed).
    <ControlPresentationProvider presentation="underlined">
      {/* Both columns take the dialog's width. They used to be
          items-center wrapping items-start, which shrink-wrapped the inner
          column to its widest child — the 800px notes field below. That made
          the content wider than the `medium` dialog (45rem) and the centring
          then clipped it at both edges, so the headings and labels lost their
          first words. Nothing here may set a width larger than its container. */}
      <div className="flex w-full flex-col">
        <div className="flex w-full flex-col items-start">
          {/* secondary, not muted: muted is for units and hints, and at this
              length it read as greyed-out body copy. */}
          <T size="base" color="secondary">
            Requesting manual quotes will instruct our dedicated logistics team
            to start working on quotes for your shipment. They're keen, eager to
            help, and have a ton of experience to make sure your shipments go
            smoothly.
          </T>
          <div className="h-[24px]" />
          <div className="flex flex-row items-start">
            <div className="flex flex-col items-start">
              <div className="-mb-2 text-base font-semibold text-foreground">
                What equipment do you need?
              </div>
              <div className="h-[5px]" />
              <BookShipmentEquipmentTypeDropdown
                equipmentType={equipmentType}
                onEquipmentTypeChange={setEquipmentType}
                enabled={true}
                errorMessage={equipmentTypeErrorMessage}
                forceValidation={forceValidation}
              />
            </div>
            <div className="w-[64px] shrink-0" />
            <div className="flex flex-col items-start">
              {equipmentType === EquipmentType.FlatBed && (
                <>
                  <div className="-mb-2 text-base font-semibold text-foreground">
                    Do you require a tarp?
                  </div>
                  <div className="h-[5px]" />
                  <BookShipmentTarpingRequiredDropdown
                    value={tarpRequired}
                    onChange={setTarpRequired}
                    enabled={true}
                    errorMessage={tarpRequiredErrorMessage}
                    forceValidation={forceValidation}
                  />
                </>
              )}
            </div>
          </div>
          <div className="h-[32px]" />
          <div className="flex flex-row items-start">
            <div className="flex flex-col items-start">
              <div className="-mb-2 text-base font-semibold text-foreground">
                Do you need shared, or dedicated use?
              </div>
              <div className="h-[5px]" />
              <BookShipmentSharedDedicatedDropdown
                value={sharedDedicated}
                onChange={setSharedDedicated}
                enabled={true}
                errorMessage={sharedDedicatedErrorMessage}
                forceValidation={forceValidation}
              />
            </div>
            <div className="w-[64px] shrink-0" />
            <div className="flex flex-col items-start">
              {(sharedDedicated === SharedDedicated.Shared ||
                sharedDedicated === SharedDedicated.Either) && (
                <>
                  <div className="-mb-2 text-base font-semibold text-foreground">
                    Linear Feet
                  </div>
                  <div className="flex flex-row items-center">
                    <DimensionInput
                      label=""
                      errorMessage={linearFeetErrorMessage}
                      forceValidation={forceValidation}
                      units={Units.Imperial}
                      dimension={Dimension.Length}
                      value={linearFeet}
                      onChange={setLinearFeet}
                      widthPx={60}
                      dataTestId="linear-feet-input"
                    />
                    <div className="w-[8px] shrink-0" />
                    <div style={{ marginTop: "-6px" }}>feet</div>
                  </div>
                </>
              )}
            </div>
          </div>
          <div className="h-[32px]" />

          {/* <PlaceholderLabel>Number of Trucks</PlaceholderLabel>
                <Stepper value={numberTrucks} onChange={setNumberTrucks} /> */}
          <div className="-mb-2 text-base font-semibold text-foreground">
            Notes
          </div>
          <div className="h-[16px]" />
          {/* w-full, not the old w-[800px]: `max-w-full` resolved against the
              shrink-wrapped column, so it capped nothing and the 800px won. */}
          <div className="w-full">
            <TextArea
              rows={4}
              value={notes}
              onChange={(e: ChangeEvent<HTMLTextAreaElement>) => {
                setNotes(e.currentTarget.value);
              }}
              placeholder="Enter any extra relevant information here that would help our team get the right quotes for you"
            />
          </div>
          <div className="h-[48px]" />
          <div className="flex flex-row items-center justify-between">
            <Button onClick={props.onCancel}>Cancel</Button>
            <div className="w-[8px] shrink-0" />
            <Button primary loading={loading} onClick={onFinished}>
              Get to work!
            </Button>
          </div>
        </div>
      </div>
    </ControlPresentationProvider>
  );
}
